import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { disconnectAppSocket, getAppSocket } from '../lib/wesSocket';
import { AlertasBlock } from '../components/reports/TableroGeneral';
import { DocumentPlusIcon, BanknotesIcon, CalculatorIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';

type CompareStat = {
  value: number;
  deltaPct: number | null;
};

type RangeStats = {
  days: number;
  revenue: { total: number; avgPerDay: number; prevTotal: number; deltaPct: number | null };
  cars: { total: number; prevTotal: number; deltaPct: number | null };
  ticket: { value: number; prevValue: number; deltaPct: number | null };
} | null;

type OverviewData = {
  period: 'today' | '7d' | '30d' | 'weekday';
  range?: RangeStats;
  revenue: {
    today: number;
    average: number;
    signal: 'good' | 'warn' | 'bad' | 'neutral';
    compareYesterday: CompareStat;
    compareWeek: CompareStat;
  };
  cars: {
    today: number;
    compareYesterday: CompareStat;
    compareWeek: CompareStat;
  };
  ticket: {
    today: number;
    compareYesterday: CompareStat;
    compareWeek: CompareStat;
  };
  live: {
    ordersOpen: number;
    ordersTotal: number;
    waitingCars: number;
    avgServiceTimeToday: number;
    avgServiceTimeOverall: number;
    avgServiceSignal: 'good' | 'warn' | 'bad' | 'neutral';
    avgServiceCountToday?: number;
    avgServiceCountOverall?: number;
  };
  services: {
    topToday: Array<{ name: string; count: number }>;
    topWeek: Array<{ name: string; count: number }>;
    topMargin: Array<{ name: string; margin: number }>;
  };
  stock: {
    lowStockCount: number;
    noMovementCount: number;
    noMovementDays: number;
    immobilizedValue: number;
    noMovementItems: Array<{
      id: string;
      name: string;
      stock: number;
      costPrice: number;
      category?: string;
    }>;
  };
  goals: {
    dailyGoal: number;
    achievedPct: number;
    projection: number;
    remaining: number;
    weeklyGoal: number;
    weeklyAchievedPct: number;
    weeklyProjection: number;
    weeklyRemaining: number;
    monthlyGoal: number;
    monthlyAchievedPct: number;
    monthlyProjection: number;
    monthlyRemaining: number;
    annualGoal: number;
    annualAchievedPct: number;
    annualProjection: number;
    annualRemaining: number;
    comparison?: {
      daily?: { achievedPctDelta: number | null; revenueDeltaPct: number | null };
      weekly?: { achievedPctDelta: number | null; revenueDeltaPct: number | null };
      monthly?: { achievedPctDelta: number | null; revenueDeltaPct: number | null };
      annual?: { achievedPctDelta: number | null; revenueDeltaPct: number | null };
    };
  };
};

type ExchangeRates = {
  oficial: {
    buy: number;
    sell: number;
  };
  blue: {
    buy: number;
    sell: number;
  };
  cripto?: {
    buy: number;
    sell: number;
  } | null;
  btc?: {
    usd: {
      buy: number;
      sell: number;
    };
    ars: {
      buy: number;
      sell: number;
    };
  } | null;
  updatedAt: string;
  stale?: boolean;
  error?: string | null;
};

// Embudo del día: de los autos que entraron, cuántos llegaron a cada paso
type EmbudoData = {
  period: 'today' | '7d' | '30d';
  desde: string;
  hasta: string;
  validityDays: number;
  pasos: Array<{ key: string; label: string; link: string; count: number; pct: number }>;
  montos: { emitido: number; borrador: number; conBorrador: number };
};

const periodOptions: Array<{ value: 'today' | '7d' | '30d'; label: string }> = [
  { value: 'today', label: 'Hoy' },
  { value: '7d', label: '7 días' },
  { value: '30d', label: '30 días' }
];

// Sección colapsable del dashboard: siempre arranca abierta; solo queda
// cerrada si el usuario la cerró a mano (estado recordado en localStorage).
const DashSection: React.FC<{
  id: string;
  title: string;
  count?: number | null;
  defaultClosedOnPhone?: boolean;
  actions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}> = ({ id, title, count, actions, className, children }) => {
  const storageKey = `dash-section-${id}`;
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved !== null) return saved === '1';
    } catch (_) { /* sin storage */ }
    return true;
  });
  const toggle = () => {
    setOpen((prev) => {
      try { localStorage.setItem(storageKey, prev ? '0' : '1'); } catch (_) { /* sin storage */ }
      return !prev;
    });
  };
  return (
    <section className={`space-y-3 ${className || ''}`}>
      <div className="flex items-center justify-between gap-2">
        <button type="button" onClick={toggle} className="flex items-center gap-1.5 text-left min-w-0">
          <h2 className="text-base font-semibold text-foreground truncate">
            {title}{typeof count === 'number' && count > 0 ? ` (${count})` : ''}
          </h2>
          <svg
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? '' : '-rotate-90'}`}
            viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"
          >
            <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {actions}
      </div>
      {open && children}
    </section>
  );
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { hasRole, hasPermission } = useAuth();
  const isAdmin = hasRole('admin');
  const canManageGoals = hasPermission('manage_settings');
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'today' | '7d' | '30d'>('today');
  const [showNoMovementModal, setShowNoMovementModal] = useState(false);

  const dailyGoalValue = overview?.goals?.dailyGoal ?? 0;
  const dailyAchievedValue = overview?.goals?.achievedPct ?? 0;
  const dailyAchievedPct = dailyGoalValue > 0
    ? Math.min(100, Math.max(0, dailyAchievedValue))
    : 0;
  const weeklyGoalValue = overview?.goals?.weeklyGoal ?? 0;
  const weeklyAchievedValue = overview?.goals?.weeklyAchievedPct ?? 0;
  const weeklyAchievedPct = weeklyGoalValue > 0
    ? Math.min(100, Math.max(0, weeklyAchievedValue))
    : 0;
  const monthlyGoalValue = overview?.goals?.monthlyGoal ?? 0;
  const monthlyAchievedValue = overview?.goals?.monthlyAchievedPct ?? 0;
  const monthlyAchievedPct = monthlyGoalValue > 0
    ? Math.min(100, Math.max(0, monthlyAchievedValue))
    : 0;
  const annualGoalValue = overview?.goals?.annualGoal ?? 0;
  const annualAchievedValue = overview?.goals?.annualAchievedPct ?? 0;
  const annualAchievedPct = annualGoalValue > 0
    ? Math.min(100, Math.max(0, annualAchievedValue))
    : 0;
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInputs, setGoalInputs] = useState({
    dailyGoal: '',
    weeklyGoal: '',
    monthlyGoal: '',
    annualGoal: ''
  });
  const [savingGoal, setSavingGoal] = useState(false);
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates | null>(null);
  const [cajaHoy, setCajaHoy] = useState<any>(null);
  const [agendaHoy, setAgendaHoy] = useState<any[] | null>(null);
  const [embudo, setEmbudo] = useState<EmbudoData | null>(null);
  const refreshTimeoutRef = useRef<number | null>(null);

  // Fecha local (no UTC: despues de las 21 el dia UTC ya es mañana)
  const hoyLocal = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // Caja de hoy + agenda de hoy (best-effort: si no hay permiso se ocultan)
  const fetchExtras = useCallback(() => {
    const hoy = hoyLocal();
    axios.get('/api/reports/tablero/caja', { params: { startDate: hoy, endDate: hoy } })
      .then((r) => setCajaHoy(r.data))
      .catch(() => setCajaHoy(null));
    axios.get(`/api/appointments/date/${hoy}`)
      .then((r) => setAgendaHoy(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAgendaHoy(null));
    // Embudo: sigue el período elegido (hoy / 7 días / 30 días)
    axios.get('/api/dashboard/embudo', { params: { period } })
      .then((r) => setEmbudo(r.data && Array.isArray(r.data.pasos) ? r.data : null))
      .catch(() => setEmbudo(null));
  }, [period]);

  const fetchOverview = useCallback(async (options?: { silent?: boolean }) => {
    const silent = Boolean(options?.silent);
    try {
      if (!silent) setLoading(true);
      const res = await axios.get('/api/dashboard/overview', {
        params: { period }
      });
      setOverview(res.data);
      fetchExtras();
    } catch (error) {
      console.error('Error fetching dashboard overview:', error);
      if (!silent) {
        setOverview(null);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [period, fetchExtras]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    const socket = getAppSocket();
    const scheduleSilentRefresh = (payload?: any) => {
      const path = payload?.path ? String(payload.path) : '';
      if (path && !(
        path.startsWith('/api/remitos') ||
        path.startsWith('/api/checks') ||
        path.startsWith('/api/current-accounts') ||
        path.startsWith('/api/cash-closures') ||
        path.startsWith('/api/service-reports') ||
        path.startsWith('/api/appointments')
      )) {
        return;
      }

      if (refreshTimeoutRef.current) window.clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = window.setTimeout(() => {
        fetchOverview({ silent: true });
      }, 400);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('cash:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('cash:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
      disconnectAppSocket();
    };
  }, [fetchOverview]);

  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        const res = await axios.get('/api/dashboard/exchange-rates');
        setExchangeRates(res.data);
      } catch (error) {
        console.error('Error fetching exchange rates:', error);
        setExchangeRates(null);
      }
    };

    fetchExchangeRates();
  }, []);

  useEffect(() => {
    if (!overview) return;
    setGoalInputs({
      dailyGoal: overview.goals.dailyGoal > 0 ? String(Math.round(overview.goals.dailyGoal)) : '',
      weeklyGoal: overview.goals.weeklyGoal > 0 ? String(Math.round(overview.goals.weeklyGoal)) : '',
      monthlyGoal: overview.goals.monthlyGoal > 0 ? String(Math.round(overview.goals.monthlyGoal)) : '',
      annualGoal: overview.goals.annualGoal > 0 ? String(Math.round(overview.goals.annualGoal)) : ''
    });
  }, [overview?.goals.dailyGoal, overview?.goals.weeklyGoal, overview?.goals.monthlyGoal, overview?.goals.annualGoal]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(value || 0);

  const formatCurrencyUsd = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0);

  const formatNumber = (value: number) =>
    new Intl.NumberFormat('es-AR', {
      maximumFractionDigits: 0
    }).format(value || 0);

  const formatPct = (value: number | null) => {
    if (value === null || Number.isNaN(value)) return 'n/a';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const formatPp = (value: number | null) => {
    if (value === null || Number.isNaN(value)) return 'n/a';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)} pp`;
  };

  const formatMinutes = (value: number) => {
    if (!value || value <= 0) return '0 min';
    const total = Math.round(value);
    if (total < 60) return `${total} min`;
    const h = Math.floor(total / 60);
    const m = total % 60;
    return m > 0 ? `${h} h ${m} min` : `${h} h`;
  };

  const formatExchangeTime = (value?: string) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    // Siempre hora argentina y formato 24hs, sin depender de la zona del dispositivo
    return `${date.toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'America/Argentina/Buenos_Aires'
    })} hs`;
  };

  const formatCategory = (value?: string) => {
    if (!value) return '-';
    const normalized = value.toString().trim().toLowerCase();
    const map: Record<string, string> = {
      servicio: 'Servicio',
      neumaticos: 'Neumaticos',
      aceites: 'Aceites',
      filtros: 'Filtros',
      frenos: 'Frenos',
      motor: 'Motor',
      transmision: 'Transmision',
      electrico: 'Electrico',
      carroceria: 'Carroceria',
      herramientas: 'Herramientas',
      consumibles: 'Consumibles',
      otros: 'Otros'
    };
    return map[normalized] || value;
  };

  const handleSaveGoal = async () => {
    const payload = {
      dailyGoal: Number(goalInputs.dailyGoal.replace(',', '.') || 0),
      weeklyGoal: Number(goalInputs.weeklyGoal.replace(',', '.') || 0),
      monthlyGoal: Number(goalInputs.monthlyGoal.replace(',', '.') || 0),
      annualGoal: Number(goalInputs.annualGoal.replace(',', '.') || 0)
    };
    if (Object.values(payload).some((value) => !Number.isFinite(value) || value < 0)) {
      toast.error('Ingresa montos validos');
      return;
    }
    try {
      setSavingGoal(true);
      await axios.post('/api/settings/goals', payload);
      await fetchOverview({ silent: true });
      setEditingGoal(false);
      toast.success('Objetivos guardados');
    } catch (error) {
      console.error('Error saving goals:', error);
      toast.error('No se pudieron guardar los objetivos');
    } finally {
      setSavingGoal(false);
    }
  };

  const revenueCardStyle = useMemo(() => {
    const signal = overview?.revenue.signal || 'neutral';
    const palette = {
      good: 'border-emerald-200 bg-emerald-50 text-emerald-900',
      warn: 'border-amber-200 bg-amber-50 text-amber-900',
      bad: 'border-red-200 bg-red-50 text-red-900',
      neutral: 'border-border bg-card text-foreground'
    };
    return palette[signal];
  }, [overview?.revenue.signal]);

  const avgTimeCardStyle = useMemo(() => {
    const signal = overview?.live.avgServiceSignal || 'neutral';
    const palette = {
      good: 'border-emerald-200 bg-emerald-50 text-emerald-900',
      warn: 'border-amber-200 bg-amber-50 text-amber-900',
      bad: 'border-red-200 bg-red-50 text-red-900',
      neutral: 'border-border bg-card text-foreground'
    };
    return palette[signal];
  }, [overview?.live.avgServiceSignal]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-40 rounded bg-muted animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, idx) => (
            <div key={idx} className="h-24 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-semibold text-foreground leading-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground">No se pudo cargar la informacion del dashboard.</p>
      </div>
    );
  }

  const rangeStats = overview.range || null;

  return (
    <div className="flex flex-col gap-6 md:gap-8">
      <div className="order-1 md:order-none flex flex-row items-center justify-between gap-4">
        <h1 className="text-2xl md:text-3xl font-semibold text-foreground leading-tight">Dashboard</h1>
        {/* Selector de período: control segmentado (Hoy por defecto) */}
        <div className="inline-flex rounded-lg border border-border overflow-hidden text-sm shrink-0">
          {periodOptions.map((option, idx) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setPeriod(option.value)}
              className={`px-3 py-1.5 whitespace-nowrap transition-colors ${idx > 0 ? 'border-l border-border' : ''} ${
                period === option.value
                  ? 'bg-foreground text-background font-medium'
                  : 'text-muted-foreground hover:bg-accent'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Embudo: de los autos que entraron, cuántos llegaron a cada paso. Va
          primero porque es lo accionable: cada paso lleva a la pantalla que lo
          resuelve. */}
      {embudo && (
        <DashSection
          id="embudo"
          title={period === 'today' ? 'Embudo de hoy' : `Embudo últimos ${period === '7d' ? 7 : 30} días`}
          className="order-2 md:order-none"
        >
          <div className="rounded-lg border bg-card shadow-sm px-3 md:px-4 py-3">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-3">
              {embudo.pasos.map((p, idx) => {
                const primero = idx === 0;
                const anterior = idx > 0 ? embudo.pasos[idx - 1].count : 0;
                const faltan = idx > 0 ? Math.max(anterior - p.count, 0) : 0;
                const barra = primero || p.pct >= 70 ? 'bg-emerald-500' : p.pct >= 40 ? 'bg-amber-500' : 'bg-red-500';
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => navigate(p.link)}
                    className="min-w-0 rounded-md px-2 py-2 text-left transition-colors hover:bg-accent/50"
                  >
                    {/* La etiqueta puede ocupar dos renglones (en el teléfono
                        "Con presupuesto" no entra en uno); la altura fija deja
                        los números alineados entre tarjetas. */}
                    <p className="min-h-[2.4em] text-[11px] uppercase leading-tight tracking-wide text-muted-foreground">{p.label}</p>
                    <p className="text-2xl font-semibold leading-tight text-foreground">{formatNumber(p.count)}</p>
                    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div className={`h-full rounded-full ${barra}`} style={{ width: `${primero ? 100 : p.pct}%` }} />
                    </div>
                    <p className="mt-1 text-xs leading-tight text-muted-foreground">
                      {primero ? (embudo.pasos[0].count === 1 ? 'auto' : 'autos') : `${p.pct}%${faltan > 0 ? ` · faltan ${faltan}` : ''}`}
                    </p>
                  </button>
                );
              })}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-border pt-3 text-xs text-muted-foreground">
              <span>Remitos emitidos: <b className="text-foreground">{formatCurrency(embudo.montos.emitido)}</b></span>
              {embudo.montos.conBorrador > 0 && (
                <button type="button" onClick={() => navigate('/remitos?filter=draft')} className="text-amber-600 underline-offset-2 hover:underline">
                  {embudo.montos.conBorrador} con remito en borrador ({formatCurrency(embudo.montos.borrador)})
                </button>
              )}
              <span className="hidden sm:inline">Checklist vigente: {embudo.validityDays} días</span>
            </div>
          </div>
        </DashSection>
      )}

      {/* Accesos rápidos (solo mobile): tiles con icono, distintos de los chips de filtro */}
      <div className="order-3 md:hidden grid grid-cols-4 gap-2">
        {hasPermission('reports') && (
          <button
            type="button"
            onClick={() => navigate('/remitos/new')}
            className="flex flex-col items-center gap-1 rounded-xl border border-border bg-card py-2.5 active:bg-accent"
          >
            <DocumentPlusIcon className="h-5 w-5 text-foreground" />
            <span className="text-[11px] font-medium leading-none">Remito</span>
          </button>
        )}
        {hasPermission('current_accounts') && (
          <button
            type="button"
            onClick={() => navigate('/current-accounts/pay')}
            className="flex flex-col items-center gap-1 rounded-xl border border-border bg-card py-2.5 active:bg-accent"
          >
            <BanknotesIcon className="h-5 w-5 text-foreground" />
            <span className="text-[11px] font-medium leading-none">Pago</span>
          </button>
        )}
        {hasPermission('reports') && (
          <button
            type="button"
            onClick={() => navigate('/cash-register')}
            className="flex flex-col items-center gap-1 rounded-xl border border-border bg-card py-2.5 active:bg-accent"
          >
            <CalculatorIcon className="h-5 w-5 text-foreground" />
            <span className="text-[11px] font-medium leading-none">Caja</span>
          </button>
        )}
        {hasPermission('appointments') && (
          <button
            type="button"
            onClick={() => navigate('/appointments-enhanced')}
            className="flex flex-col items-center gap-1 rounded-xl border border-border bg-card py-2.5 active:bg-accent"
          >
            <CalendarDaysIcon className="h-5 w-5 text-foreground" />
            <span className="text-[11px] font-medium leading-none">Turnos</span>
          </button>
        )}
      </div>

      {exchangeRates && (
        <div className="order-8 md:order-none flex items-center gap-x-3 md:gap-x-4 rounded-lg border border-border bg-card px-3 md:px-4 py-2 text-xs md:text-sm overflow-x-auto no-scrollbar whitespace-nowrap">
          <span className="text-xs uppercase tracking-wide text-muted-foreground shrink-0">Dólar</span>
          <span className="shrink-0">
            Oficial <b>{formatCurrency(exchangeRates.oficial.buy)}</b> / <b>{formatCurrency(exchangeRates.oficial.sell)}</b>
          </span>
          <span className="shrink-0">
            Blue <b>{formatCurrency(exchangeRates.blue.buy)}</b> / <b>{formatCurrency(exchangeRates.blue.sell)}</b>
          </span>
          {exchangeRates.stale && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600 text-xs font-bold">!</span>
              </TooltipTrigger>
              <TooltipContent>
                No se pudo actualizar la cotizacion. Mostrando el ultimo valor disponible.
              </TooltipContent>
            </Tooltip>
          )}
          {/* En el teléfono el "Actualizado..." se omite para que todo entre sin scroll */}
          {exchangeRates.updatedAt && (
            <span className="ml-auto shrink-0 text-xs text-muted-foreground hidden sm:inline">
              Actualizado {formatExchangeTime(exchangeRates.updatedAt)}
            </span>
          )}
        </div>
      )}

      <DashSection
        id="resultado"
        title={rangeStats ? `Resultado últimos ${rangeStats.days} días` : 'Resultado del dia'}
        className="order-4 md:order-none"
      >
        {rangeStats ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            <div className="col-span-2 lg:col-span-1 rounded-lg border bg-card px-4 py-3 shadow-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Facturación últimos {rangeStats.days} días</p>
              <p className="text-2xl font-semibold text-foreground">{formatCurrency(rangeStats.revenue.total)}</p>
              <p className="text-xs text-muted-foreground">Promedio por día: {formatCurrency(rangeStats.revenue.avgPerDay)}</p>
              <div className="mt-2 text-xs text-muted-foreground">
                <div>Vs {rangeStats.days} días anteriores: {formatPct(rangeStats.revenue.deltaPct)} ({formatCurrency(rangeStats.revenue.prevTotal)})</div>
              </div>
            </div>

            <div className="rounded-lg border bg-card px-3 md:px-4 py-3 shadow-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Autos atendidos</p>
              <p className="text-2xl font-semibold text-foreground">{formatNumber(rangeStats.cars.total)}</p>
              <div className="mt-2 text-xs text-muted-foreground">
                <div>Vs anteriores: {formatPct(rangeStats.cars.deltaPct)}</div>
              </div>
            </div>

            <div className="rounded-lg border bg-card px-3 md:px-4 py-3 shadow-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Ticket promedio</p>
              <p className="text-2xl font-semibold text-foreground">{formatCurrency(rangeStats.ticket.value)}</p>
              <p className="text-xs text-muted-foreground">Por remito emitido</p>
              <div className="mt-2 text-xs text-muted-foreground">
                <div>Vs anteriores: {formatPct(rangeStats.ticket.deltaPct)}</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            <div className={`col-span-2 lg:col-span-1 rounded-lg border px-4 py-3 shadow-sm ${revenueCardStyle}`}>
              <p className="text-xs uppercase tracking-wide opacity-70">Facturacion hoy (remitos)</p>
              <p className="text-2xl font-semibold">{formatCurrency(overview.revenue.today)}</p>
              <p className="text-xs opacity-70">Promedio 7 días: {formatCurrency(overview.revenue.average)}</p>
              <div className="mt-2 text-xs">
                <div>Vs ayer: {formatPct(overview.revenue.compareYesterday.deltaPct)}</div>
                <div>Vs mismo dia semana pasada: {formatPct(overview.revenue.compareWeek.deltaPct)}</div>
              </div>
            </div>

            <div className="rounded-lg border bg-card px-3 md:px-4 py-3 shadow-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Autos atendidos hoy</p>
              <p className="text-2xl font-semibold text-foreground">{formatNumber(overview.cars.today)}</p>
              <div className="mt-2 text-xs text-muted-foreground">
                <div>Vs ayer: {formatPct(overview.cars.compareYesterday.deltaPct)}</div>
                <div className="hidden sm:block">Vs mismo dia semana pasada: {formatPct(overview.cars.compareWeek.deltaPct)}</div>
              </div>
            </div>

            <div className="rounded-lg border bg-card px-3 md:px-4 py-3 shadow-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Ticket promedio hoy</p>
              <p className="text-2xl font-semibold text-foreground">{formatCurrency(overview.ticket.today)}</p>
              <p className="text-xs text-muted-foreground">Por remito emitido</p>
              <div className="mt-2 text-xs text-muted-foreground">
                <div>Vs ayer: {formatPct(overview.ticket.compareYesterday.deltaPct)}</div>
                <div className="hidden sm:block">Vs mismo dia semana pasada: {formatPct(overview.ticket.compareWeek.deltaPct)}</div>
              </div>
            </div>
          </div>
        )}
      </DashSection>

      <DashSection id="mirar" title="Para mirar hoy" className="order-5 md:order-none">
        <AlertasBlock onNavigate={(tab) => navigate(`/reports?tab=${tab}`)} />
      </DashSection>

      {cajaHoy && (
        <DashSection id="caja" title="Caja de hoy" className="order-7 md:order-none">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            <div className="rounded-lg border bg-card px-3 md:px-4 py-3 shadow-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Cobrado hoy</p>
              <p className="text-2xl font-semibold text-foreground">{formatCurrency(cajaHoy.kpis?.cobrado?.value || 0)}</p>
              <p className="text-xs text-muted-foreground">Sin cta. cte. · según lo cargado</p>
            </div>
            <div className="rounded-lg border bg-card px-3 md:px-4 py-3 shadow-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Egresos hoy</p>
              <p className="text-2xl font-semibold text-foreground">{formatCurrency(cajaHoy.kpis?.egresos?.value || 0)}</p>
              <p className="text-xs text-muted-foreground">Compras recibidas + gastos operativos</p>
            </div>
            <div className="col-span-2 lg:col-span-1 rounded-lg border bg-card px-3 md:px-4 py-3 shadow-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Por medio de pago</p>
              <div className="mt-1 space-y-1 text-sm">
                {(cajaHoy.mediosPago || []).length ? (
                  cajaHoy.mediosPago.slice(0, 4).map((m: any) => (
                    <div key={m.code} className="flex items-center justify-between">
                      <span className="truncate text-muted-foreground">{m.name}</span>
                      <span className="font-semibold">{formatCurrency(m.monto)}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground text-sm">Sin cobros hoy</p>
                )}
              </div>
            </div>
          </div>
        </DashSection>
      )}

      {agendaHoy && (
        <DashSection
          id="agenda"
          title="Agenda de hoy"
          count={agendaHoy.filter((a: any) => a.status !== 'cancelled').length}
          className="order-6 md:order-none"
          actions={(
            <Button variant="outline" size="sm" className="h-8 px-3 shrink-0" onClick={() => navigate('/appointments-enhanced')}>
              Ver turnos
            </Button>
          )}
        >
          {agendaHoy.length === 0 ? (
            <div className="rounded-lg border bg-card px-4 py-3 text-sm text-muted-foreground">
              Sin turnos agendados para hoy.
            </div>
          ) : (
            <div className="rounded-lg border bg-card divide-y divide-border">
              {[...agendaHoy]
                .filter((a: any) => a.status !== 'cancelled')
                .sort((a: any, b: any) => String(a.timeSlot || '').localeCompare(String(b.timeSlot || '')))
                .slice(0, 8)
                .map((a: any) => (
                  <div key={a.id} className="px-4 py-2 text-sm">
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-semibold w-12 shrink-0">{a.timeSlot || '—'}</span>
                      <span className="font-medium truncate">{a.clientName || 'Sin cliente'}</span>
                      <span className="text-muted-foreground truncate hidden sm:inline">
                        {a.vehiclePlate ? `${a.vehiclePlate} · ` : ''}{a.serviceType || a.description || ''}
                      </span>
                      <span className={`ml-auto shrink-0 text-xs px-2 py-0.5 rounded-full ${
                        a.status === 'completed' ? 'bg-emerald-500/10 text-emerald-600'
                          : a.status === 'in_progress' ? 'bg-amber-500/10 text-amber-600'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {a.status === 'completed' ? 'Terminado' : a.status === 'in_progress' ? 'En curso' : a.status === 'confirmed' ? 'Confirmado' : 'Agendado'}
                      </span>
                    </div>
                    {/* En el teléfono la patente y el servicio van en un segundo renglón (antes se ocultaban) */}
                    {(a.vehiclePlate || a.serviceType || a.description) && (
                      <div className="sm:hidden pl-[60px] text-xs text-muted-foreground truncate">
                        {a.vehiclePlate ? `${a.vehiclePlate} · ` : ''}{a.serviceType || a.description || ''}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}
        </DashSection>
      )}

      <DashSection id="vivo" title="Operacion en vivo" defaultClosedOnPhone className="order-9 md:order-none">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          <div className="rounded-lg border bg-card px-3 md:px-4 py-3 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Ordenes</p>
            <p className="text-2xl font-semibold text-foreground">
              {formatNumber(overview.live.ordersOpen)}/{formatNumber(overview.live.ordersTotal)}
            </p>
            <p className="text-xs text-muted-foreground">Abiertas / Totales (hoy)</p>
          </div>

          <div className="rounded-lg border bg-card px-3 md:px-4 py-3 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Autos en espera</p>
            <p className="text-2xl font-semibold text-foreground">{formatNumber(overview.live.waitingCars)}</p>
            <p className="text-xs text-muted-foreground">Turnos pendientes o confirmados</p>
          </div>

          <div className={`col-span-2 lg:col-span-1 rounded-lg border px-3 md:px-4 py-3 shadow-sm ${avgTimeCardStyle}`}>
            <p className="text-xs uppercase tracking-wide opacity-70">Tiempo en taller</p>
            <p className="text-2xl font-semibold">{formatMinutes(overview.live.avgServiceTimeToday)}</p>
            <p className="text-xs opacity-70">
              Del ingreso a la entrega · {formatNumber(overview.live.avgServiceCountToday || 0)} OT hoy
            </p>
            <p className="text-xs opacity-70">
              Promedio 30 días: {formatMinutes(overview.live.avgServiceTimeOverall)} ({formatNumber(overview.live.avgServiceCountOverall || 0)} OT)
            </p>
          </div>
        </div>
      </DashSection>

      <DashSection id="mix" title="Mix de servicios" defaultClosedOnPhone className="order-10 md:order-none">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
          <div className="rounded-lg border bg-card px-4 py-3 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Más vendidos hoy · ítems de remito</p>
            <div className="mt-2 space-y-2 text-sm">
              {overview.services.topToday.length ? (
                overview.services.topToday.map((item) => (
                  <div key={item.name} className="flex items-center justify-between">
                    <span className="truncate">{item.name}</span>
                    <span className="font-semibold">{item.count}</span>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-sm">Sin datos</p>
              )}
            </div>
          </div>

          <div className="rounded-lg border bg-card px-4 py-3 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Más vendidos últimos 7 días</p>
            <div className="mt-2 space-y-2 text-sm">
              {overview.services.topWeek.length ? (
                overview.services.topWeek.map((item) => (
                  <div key={item.name} className="flex items-center justify-between">
                    <span className="truncate">{item.name}</span>
                    <span className="font-semibold">{item.count}</span>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-sm">Sin datos</p>
              )}
            </div>
          </div>

          <div className="rounded-lg border bg-card px-4 py-3 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Mejor margen 7 días · ítems con costo</p>
            <div className="mt-2 space-y-2 text-sm">
              {overview.services.topMargin.length ? (
                overview.services.topMargin.map((item) => (
                  <div key={item.name} className="flex items-center justify-between">
                    <span className="truncate">{item.name}</span>
                    <span className="font-semibold">{formatCurrency(item.margin)}</span>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-sm">Sin datos</p>
              )}
            </div>
          </div>
        </div>
      </DashSection>

      <DashSection
        id="stock"
        title="Alertas de stock"
        count={overview.stock.lowStockCount}
        defaultClosedOnPhone
        className="order-11 md:order-none"
      >
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          <div className="rounded-lg border bg-card px-3 md:px-4 py-3 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Stock bajo</p>
            <p className="text-2xl font-semibold text-foreground">
              {formatNumber(overview.stock.lowStockCount)}
            </p>
            <p className="text-xs text-muted-foreground">Productos criticos</p>
          </div>

          <div className="rounded-lg border bg-card px-3 md:px-4 py-3 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Sin movimientos</p>
            <p className="text-2xl font-semibold text-foreground">
              {formatNumber(overview.stock.noMovementCount)}
            </p>
            <p className="text-xs text-muted-foreground">
              En los ultimos {overview.stock.noMovementDays} dias
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 h-8 px-3"
              onClick={() => setShowNoMovementModal(true)}
              disabled={!overview.stock.noMovementCount}
            >
              Ver detalle
            </Button>
          </div>

          <div className="col-span-2 lg:col-span-1 rounded-lg border bg-card px-3 md:px-4 py-3 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Stock inmovilizado</p>
            <p className="text-2xl font-semibold text-foreground">
              {formatCurrency(overview.stock.immobilizedValue)}
            </p>
            <p className="text-xs text-muted-foreground">Valor de inventario</p>
          </div>
        </div>
      </DashSection>

      <DashSection
        id="objetivos"
        title="Objetivos"
        defaultClosedOnPhone
        className="order-12 md:order-none"
        actions={canManageGoals ? (
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-3 shrink-0"
            onClick={() => setEditingGoal((prev) => !prev)}
          >
            {editingGoal ? 'Cerrar edicion' : 'Configurar objetivos'}
          </Button>
        ) : undefined}
      >
        {canManageGoals && editingGoal && (
          <div className="rounded-lg border bg-card p-4 space-y-3">
            <p className="text-sm font-medium text-foreground">Editar objetivos (ARS)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">
              <input
                type="number"
                inputMode="numeric"
                value={goalInputs.dailyGoal}
                onChange={(event) => setGoalInputs((prev) => ({ ...prev, dailyGoal: event.target.value }))}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
                placeholder="Objetivo diario"
              />
              <input
                type="number"
                inputMode="numeric"
                value={goalInputs.weeklyGoal}
                onChange={(event) => setGoalInputs((prev) => ({ ...prev, weeklyGoal: event.target.value }))}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
                placeholder="Objetivo semanal"
              />
              <input
                type="number"
                inputMode="numeric"
                value={goalInputs.monthlyGoal}
                onChange={(event) => setGoalInputs((prev) => ({ ...prev, monthlyGoal: event.target.value }))}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
                placeholder="Objetivo mensual"
              />
              <input
                type="number"
                inputMode="numeric"
                value={goalInputs.annualGoal}
                onChange={(event) => setGoalInputs((prev) => ({ ...prev, annualGoal: event.target.value }))}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
                placeholder="Objetivo anual"
              />
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                className="h-8 px-3"
                onClick={handleSaveGoal}
                disabled={savingGoal}
              >
                {savingGoal ? 'Guardando...' : 'Guardar objetivos'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-3"
                onClick={() => {
                  setEditingGoal(false);
                  setGoalInputs({
                    dailyGoal: overview.goals.dailyGoal > 0 ? String(Math.round(overview.goals.dailyGoal)) : '',
                    weeklyGoal: overview.goals.weeklyGoal > 0 ? String(Math.round(overview.goals.weeklyGoal)) : '',
                    monthlyGoal: overview.goals.monthlyGoal > 0 ? String(Math.round(overview.goals.monthlyGoal)) : '',
                    annualGoal: overview.goals.annualGoal > 0 ? String(Math.round(overview.goals.annualGoal)) : ''
                  });
                }}
              >
                Cancelar
              </Button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
          {([
            {
              key: 'daily', label: 'Objetivo diario', goal: overview.goals.dailyGoal,
              achieved: overview.goals.achievedPct, pct: dailyAchievedPct,
              remaining: overview.goals.remaining, projection: overview.goals.projection,
              comparison: overview.goals.comparison?.daily
            },
            {
              key: 'weekly', label: 'Objetivo semanal', goal: overview.goals.weeklyGoal,
              achieved: overview.goals.weeklyAchievedPct, pct: weeklyAchievedPct,
              remaining: overview.goals.weeklyRemaining, projection: overview.goals.weeklyProjection,
              comparison: overview.goals.comparison?.weekly
            },
            {
              key: 'monthly', label: 'Objetivo mensual', goal: overview.goals.monthlyGoal,
              achieved: overview.goals.monthlyAchievedPct, pct: monthlyAchievedPct,
              remaining: overview.goals.monthlyRemaining, projection: overview.goals.monthlyProjection,
              comparison: overview.goals.comparison?.monthly
            },
            {
              key: 'annual', label: 'Objetivo anual', goal: overview.goals.annualGoal,
              achieved: overview.goals.annualAchievedPct, pct: annualAchievedPct,
              remaining: overview.goals.annualRemaining, projection: overview.goals.annualProjection,
              comparison: overview.goals.comparison?.annual
            }
          ]).map((g) => (
            <div key={g.key} className="rounded-lg border bg-card px-3 md:px-4 py-3 shadow-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">{g.label}</p>
              <p className="text-lg md:text-2xl font-semibold text-foreground">
                {g.goal > 0 ? formatCurrency(g.goal) : 'Sin objetivo'}
              </p>
              <p className="mt-2 text-sm text-foreground">
                Alcanzado: {g.goal > 0 ? `${g.achieved.toFixed(1)}%` : 'n/a'}
              </p>
              <div className="mt-2 h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-all"
                  style={{ width: `${g.pct}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Faltan: {g.goal > 0 ? formatCurrency(g.remaining) : 'n/a'}
              </p>
              <p className="text-xs text-muted-foreground">
                Proyeccion: {formatCurrency(g.projection)}
              </p>
              <p className="hidden sm:block text-xs text-muted-foreground">
                Vs periodo anterior: {formatPp(g.comparison?.achievedPctDelta ?? null)} | Ingreso {formatPct(g.comparison?.revenueDeltaPct ?? null)}
              </p>
            </div>
          ))}
        </div>
      </DashSection>

      <Dialog open={showNoMovementModal} onOpenChange={setShowNoMovementModal}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Productos sin movimiento</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">
            Ultimos {overview.stock.noMovementDays} dias sin remitos.
          </div>
          <div className="mt-4 max-h-[60vh] overflow-auto">
            {/* En el teléfono: tarjetas (la tabla de 5 columnas no entra) */}
            {overview.stock.noMovementItems.length > 0 && (
              <div className="sm:hidden space-y-2">
                {overview.stock.noMovementItems.map((item) => {
                  const cost = Number(item.costPrice || 0);
                  const stock = Number(item.stock || 0);
                  const totalValue = cost > 0 ? cost * stock : 0;
                  return (
                    <div key={item.id} className="rounded-lg border bg-card p-3">
                      <div className="font-medium text-sm">{item.name}</div>
                      <div className="text-xs text-muted-foreground">{formatCategory(item.category)}</div>
                      <div className="mt-1 flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Stock: <b className="text-foreground">{formatNumber(stock)}</b></span>
                        <span className="font-semibold">{totalValue > 0 ? formatCurrency(totalValue) : '—'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {overview.stock.noMovementItems.length ? (
              <div className="hidden sm:block">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-muted/50 border-b">
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Producto</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Categoria</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-right">Stock</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-right">Costo</TableHead>
                    <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-right">Valor stock</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overview.stock.noMovementItems.map((item) => {
                    const cost = Number(item.costPrice || 0);
                    const stock = Number(item.stock || 0);
                    const totalValue = cost > 0 ? cost * stock : 0;
                    return (
                      <TableRow key={item.id} className="border-b hover:bg-muted/50">
                        <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                          <div className="font-medium">{item.name}</div>
                        </TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-left">
                          {formatCategory(item.category)}
                        </TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-right">
                          {formatNumber(stock)}
                        </TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-right">
                          {cost > 0 ? formatCurrency(cost) : '-'}
                        </TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-right">
                          {totalValue > 0 ? formatCurrency(totalValue) : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                No hay productos sin movimiento.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;

